import React, { memo, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import PriceSection from "pages/OrderModal/components/PriceSection/PriceSection";
import ConfirmModal from "components/ConfirmModal";
import { IAddressStep } from "pages/OrderModal/components/CreatedOrder/types";
import {
  findCancelOrderResult,
  initialAddressSteps,
  orderCloseReason,
} from "pages/OrderModal/components/CreatedOrder/constants";
import { useCancelOrderMutation, useGetOrderDetailsQuery } from "store/api";
import { useAppSelector } from "store/hooks/useAppSelector";
import { shallowEqual } from "react-redux";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import MoneyIcon from "@mui/icons-material/Money";
import { formatDateTime } from "utils/formatDateTime";
import { getAddressSteps } from "./constants";
import { paymentTypes } from "pages/OrderModal/components/PaymentSection/constants";
import { removeOrderDetails } from "store/slices/orderDetails";
import {
  removeComment,
  removeEntrance,
  removeSrcAddress,
  removeSrcAddressRoute,
  removeSrcBuilding,
} from "store/slices/srcAddress";
import { clearBoxes, setNoRoutes } from "store/slices/dstAddress";
import {
  setCheckedOptions,
  setCustomCheckedOptions,
} from "store/slices/options";
import { setInitialTaxiClass } from "store/slices/taxiClass";
import { removeArriveTime } from "store/slices/arriveTime";
import { setDefaultPayment } from "store/slices/payment";
import { removeCost } from "store/slices/orderCost";
import { setTariffs } from "store/slices/tariffs";
import useNotificationToast from "hooks/useNotificationToast/useNotificationToast";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import createdOrderStyles from "pages/OrderModal/components/CreatedOrder/CreatedOrder.styled";
import styles from "./SearchModal.styled";

const SearchModal = () => {
  const [addressSteps, setAddressSteps] =
    useState<IAddressStep[]>(initialAddressSteps);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const orderId = useAppSelector(
    (state) => state.orderDetails.id,
    shallowEqual,
  );
  const dispatch = useAppDispatch();
  const [cancelOrder] = useCancelOrderMutation();
  const { data: orderDetails, isSuccess } = useGetOrderDetailsQuery(
    orderId || "",
    {
      skip: !orderId,
    },
  );

  const steps = useMemo(() => {
    return orderDetails
      ? getAddressSteps(orderDetails.route)
      : initialAddressSteps;
  }, [orderDetails]);

  useEffect(() => {
    setAddressSteps(steps);
  }, [steps]);

  const handleToggleModal = (): void => {
    setIsModalOpen((prev) => !prev);
  };

  const handleOrderCancel = async () => {
    if (orderId) {
      try {
        await cancelOrder(orderId).unwrap();
        dispatch(removeSrcAddressRoute());
        dispatch(removeOrderDetails());
        dispatch(removeSrcBuilding());
        dispatch(removeSrcAddress());
        dispatch(removeComment());
        dispatch(removeEntrance());
        dispatch(removeArriveTime());
        dispatch(setDefaultPayment());
        dispatch(clearBoxes());
        dispatch(setCheckedOptions([]));
        dispatch(setCustomCheckedOptions([]));
        dispatch(setNoRoutes(false));
        dispatch(setInitialTaxiClass());
        dispatch(removeCost());
        dispatch(setTariffs([]));
      } catch (error) {
        useNotificationToast({
          type: "error",
          message: findCancelOrderResult(error.data.order_client_cancel_result)
            ?.description,
        });
      }
    }
  };

  return (
    <Box sx={styles.contentWrap}>
      <Typography component="h2" sx={styles.title}>
        Пошук машини
      </Typography>
      <Stepper orientation="vertical" sx={styles.stepper}>
        {addressSteps.map((step) => (
          <Step key={step.label}>
            <StepLabel
              StepIconComponent={() => <CircleOutlinedIcon />}
              sx={styles.stepLabel}
            >
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={styles.details}>
        {isSuccess && (
          <Typography sx={styles.time}>{`${
            orderDetails.required_time
              ? formatDateTime(orderDetails.required_time)
              : "На зараз"
          }`}</Typography>
        )}
        <Box sx={styles.payment}>
          <MoneyIcon color="success" />
          {isSuccess && (
            <Typography>{`${
              orderDetails.payment_type === paymentTypes[2].value
                ? "Картка"
                : orderDetails.pay_bonuses
                ? "Бонуси"
                : "Готівка"
            }`}</Typography>
          )}
        </Box>
      </Box>
      <Box sx={styles.footer}>
        {isSuccess && <PriceSection />}
        <Button
          type="button"
          className="secondary"
          sx={createdOrderStyles.btn}
          onClick={handleToggleModal}
          disabled={orderCloseReason.some(
            (reason) => orderDetails?.close_reason === reason.value,
          )}
        >
          Скасувати
        </Button>
      </Box>
      <ConfirmModal
        openModal={isModalOpen}
        modalClose={handleToggleModal}
        handleAction={handleOrderCancel}
        titleMessage="Скасувати замовлення?"
        buttonMessage="Так"
        secondBtnMessage="Ні"
      />
    </Box>
  );
};

export default memo(SearchModal);
